import {
  PERFORMANCE_COACH_LABEL,
  CC_LABEL,
  EMPTY_PERFORMANCE_COACH_MESSAGE,
  EMPTY_STATE_PERFORMANCE_COACH_MESSAGE,
  EMPTY_STRING,
  INTERNAL_GRADE_LABEL,
  NAME_SURNAME_EMAIL_LABEL,
  PRACTICE_LABEL,
} from "../../constants/Dictionary";
import {
  AlignmentEnum,
  FilterProps,
  FontWeightEnum,
  PageNames,
} from "../../types/AppTypes";
import careerManagerImage from "../../images/careerManager.png";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { StyledCareerManagers } from "./CareerManagers.styled";
import { DoubleStructure } from "../../components/core/DoubleStructure/DoubleStructure";
import { BodyTyp } from "../../components/Typography/Typography";
import SearchInput from "../../components/core/SearchInput/SearchInput";
import React, { useEffect, useRef, useState } from "react";
import { FiltersPanel } from "../../components/FiltersPanel/FiltersPanel";
import { useLocation, useNavigate } from "react-router-dom";
import { CAREER_MANAGERS_PAGE_SIZE } from "../../constants/AppConstants";
import { careerManagerColumns } from "../../constants/TableColumns";
import { FiltersSelected } from "../../components/FiltersSelected/FiltersSelected";
import { Table } from "../../components/core/Table/Table";
import { useAppSelector } from "../../hooks/store.hook";
import { getCareerManagersFiltersTitles } from "../../constants/FilterTitles";
import { useBreadCrumb } from "../../hooks/useBreadCrumbs.hooks";
import {
  DETAIL_QUERY_PARAM_KEY,
  encodeParams,
} from "../../utils/queryParams.utils";
import { CareerManager } from "../../types/api/careerManagers.type";
import {
  setSearchCareerManagers,
  setFiltersCareerManagers,
  setPaginationCareerManagers,
} from "../../stores/actions/careerManager.actions";
import { useDispatch } from "react-redux";
import { useDebounce } from "use-debounce";
import { Loader } from "../../components/core/Loader/Loader";
import LoadingThumbnail from "../../components/LoadingThumbnail/LoadingThumbnail";
import {
  useCareerManagers,
  useCareerManagersFilters,
} from "../../hooks/careerManagers.hook";
import { useDimensions } from "../../hooks/useDimensions.hook";
import { TextBoxGrid } from "../../components/TextBoxGrid/TextBoxGrid.component";
import { findCCById, findPracticeById } from "../../utils/orgFunctions.utils";

export const CareerManagers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [hasData, setHasData] = useState<boolean | null>(null);
  const { profile } = useAppSelector((state) => state.user);

  const { isMobile } = useDimensions();

  const { orgSettings } = useAppSelector((state) => state.config);
  const {
    filtersCareerManagers,
    searchCareerManagers,
    paginationCareerManagers,
  } = useAppSelector((state) => state.careerManager);

  const [debounceSearchCareerManager] = useDebounce(searchCareerManagers, 500);

  const { data, isLoading, isFetching } = useCareerManagers({
    paginationCareerManagers,
    filtersCareerManagers,
    debounceSearchCareerManager,
  });

  const { data: filtersData, isLoading: filterLoading } =
    useCareerManagersFilters();

  const handleFilters = (filters: FilterProps[]) => {
    dispatch(setPaginationCareerManagers(0));
    dispatch(setFiltersCareerManagers(filters));
  };

  useBreadCrumb([
    { name: PERFORMANCE_COACH_LABEL, pathName: PageNames.CAREER_MANAGER },
  ]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch(setPaginationCareerManagers(0));
  }, [debounceSearchCareerManager]);

  const joinFilters = Object.values(filtersData ?? []).flat();
  const filtersAreActive = filtersCareerManagers.length > 0;

  useEffect(() => {
    if (data && hasData === null) {
      if (filtersAreActive || searchCareerManagers.length > 0) {
        setHasData(true);
        return;
      }
      const newHasData = data.pagination.total > 0;
      setHasData(newHasData);
    }
  }, [data, hasData]);

  const expandedRow = (data: CareerManager): React.ReactElement | null => {
    const orgArea = orgSettings.areaList.all;
    return (
      <TextBoxGrid
        numCol={2}
        items={[
          {
            label: INTERNAL_GRADE_LABEL,
            text: data.internalGrade ?? EMPTY_STRING,
          },
          {
            label: PRACTICE_LABEL,
            text:
              findPracticeById(orgArea, data.practice)?.name ?? EMPTY_STRING,
          },
          {
            label: CC_LABEL,
            text: findCCById(orgArea, data.cc)?.name ?? EMPTY_STRING,
          },
        ]}
      />
    );
  };

  return (
    <StyledCareerManagers>
      {isLoading && hasData === null ? (
        <Loader loading={true}>
          <LoadingThumbnail marginTop={316} />
        </Loader>
      ) : null}
      {!isLoading && hasData === false ? (
        <div className="empty-state">
          <EmptyState
            imageSrc={careerManagerImage}
            label={EMPTY_STATE_PERFORMANCE_COACH_MESSAGE}
          />
        </div>
      ) : null}
      {hasData ? (
        <>
          <div className="header-container">
            <DoubleStructure
              verticalAlignment={AlignmentEnum.CENTER}
              variant="primary"
            >
              <div>
                {data && (
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                    {`${data?.pagination.total.toString()} ${PERFORMANCE_COACH_LABEL}`}
                  </BodyTyp>
                )}
              </div>
              <div className="filters-container">
                <div className="search-container">
                  <SearchInput
                    value={searchCareerManagers}
                    onChange={(value) =>
                      dispatch(setSearchCareerManagers(value))
                    }
                    label={NAME_SURNAME_EMAIL_LABEL}
                    name={"search"}
                  />
                </div>
                <FiltersPanel
                  data={filtersData}
                  setSelectedFilters={handleFilters}
                  selectedFilters={filtersCareerManagers}
                  filtersTitles={getCareerManagersFiltersTitles()}
                  defaultExpanded
                  loading={filterLoading}
                />
              </div>
            </DoubleStructure>
            <FiltersSelected
              data={joinFilters}
              filters={filtersCareerManagers}
              setFilters={handleFilters}
            />
          </div>
          <div className="table-container">
            <Table<CareerManager>
              loading={isFetching}
              headers={careerManagerColumns(orgSettings.areaList.all, isMobile)}
              data={data?.data ?? []}
              scrollY={`calc(100dvh - ${filtersAreActive ? "352px" : "300px"})`}
              onRow={(record) => {
                navigate(
                  `${pathname}/${record.id}?${DETAIL_QUERY_PARAM_KEY}=${encodeParams(
                    {
                      careerManagerName: record.name,
                      careerManagerSurname: record.surname,
                    },
                  )}`,
                );
              }}
              clickableRow={(record) =>
                !profile.restrictedCollaboratorIds.includes(record.id)
              }
              pagination={{
                current: paginationCareerManagers + 1,
                onChange: (pageNumber) =>
                  dispatch(setPaginationCareerManagers(pageNumber - 1)),
                total: data?.pagination.total ?? 0,
                pageSize: CAREER_MANAGERS_PAGE_SIZE,
              }}
              emptyText={EMPTY_PERFORMANCE_COACH_MESSAGE}
              showRowAction={!isMobile}
              expandable={
                isMobile ? { expandedRowRender: expandedRow } : undefined
              }
            />
          </div>
        </>
      ) : null}
    </StyledCareerManagers>
  );
};
